// ============================================================================
// Viewport virtual del preview (#/personalizar-tienda).
//
// La preview monta el STOREFRONT REAL dentro de un iframe srcdoc. Para que
// sus media queries evalúen contra un viewport LÓGICO (y no contra el ancho
// físico del panel, que en Desktop puede colapsar < 821px y activar la
// hamburguesa), el iframe vive en un "viewport virtual" de ancho fijo por
// dispositivo y el conjunto se ESCALA visualmente para caber en el panel:
//
//   lienzo (área disponible del editor)
//     └── .pf-ed__viewport   ancho lógico fijo (sin escalar)
//           └── iframe       viewport = ancho lógico (media queries reales)
//
// La escala es exclusivamente visual (`transform: scale`): el layout del
// iframe y sus media queries resuelven SIEMPRE contra el ancho lógico
// (1280 / 768 / 390px), igual que en la tienda pública.
// ============================================================================

export const VIEWPORTS_DISPOSITIVO = {
  desktop: { nombre: 'Desktop', ancho: 1280, alto: 720 },
  tablet: { nombre: 'Tablet', ancho: 768, alto: 720 },
  mobile: { nombre: 'Móvil', ancho: 390, alto: 720 },
};

export const DISPOSITIVOS = Object.entries(VIEWPORTS_DISPOSITIVO).map(([id, d]) => ({
  id,
  nombre: d.nombre,
  ancho: d.ancho,
  alto: d.alto,
}));

/**
 * Factor visual para que el viewport lógico quepa en el área disponible.
 * Devuelve `null` cuando no hay espacio medible (se omite la escala y se usa
 * escala 1). Nunca amplía más grande que el tamaño real (máx. 1).
 * @param {number} anchoDisponible - ancho libre real del lienzo, en px
 * @param {number} anchoLogico - ancho del viewport lógico del dispositivo
 * @returns {number|null}
 */
export function calcularEscala(anchoDisponible, anchoLogico) {
  if (!(anchoDisponible > 0) || anchoLogico <= 0) return null;
  return Math.min(1, anchoDisponible / anchoLogico);
}

/**
 * Aplica el viewport virtual al lienzo y viewport del dispositivo activo.
 *
 * Fija el ancho/alto LÓGICO en el `.pf-ed__viewport` (así el iframe del
 * storefront toma ese viewport) y calcula `--sp-ed-escala` desde el espacio
 * libre del lienzo. El lienzo reserva la altura visual resultante.
 *
 * @param {{ lienzo: HTMLElement, viewport: HTMLElement, dispositivo: string, anchoDisponible?: number }} opciones
 * @returns {{ dispositivo: string, ancho: number, alto: number, escala: number }|null}
 *          null si el dispositivo/lienzo/viewport no existen o no hay espacio medible.
 */
export function aplicarViewportDispositivo({ lienzo, viewport, dispositivo, anchoDisponible }) {
  const definicion = VIEWPORTS_DISPOSITIVO[dispositivo];
  if (!definicion || !lienzo || !viewport) return null;

  viewport.style.setProperty('--sp-ed-viewport-ancho', `${definicion.ancho}px`);
  viewport.style.setProperty('--sp-ed-viewport-alto', `${definicion.alto}px`);

  const espacioLibre = anchoDisponible ?? lienzo.clientWidth ?? 0;
  const escala = calcularEscala(espacioLibre, definicion.ancho);
  if (escala === null) return null;

  viewport.style.setProperty('--sp-ed-escala', String(escala));
  lienzo.style.height = `${Math.round(definicion.alto * escala)}px`;

  return { dispositivo, ancho: definicion.ancho, alto: definicion.alto, escala };
}
